/**
 * Shared utilities for Installation Questionnaire validation
 * Used by VisitInstallationQuestionnaire.jsx and ServiceVisits.jsx
 */

// Required General section questions (1-3) - only these are mandatory for completion
export const REQUIRED_QUESTIONS = [1, 2, 3];

// Question texts for error messages
export const QUESTION_TEXTS = {
    1: "Estado físico del empaque",
    2: "Equipo en sitio de instalación",
    3: "Dimensiones de nicho son correctas"
};

/**
 * Check if a question has a valid answer
 * @param {Object} answers - The answers object from questionnaire
 * @param {number|string} questionId - The question ID to check
 * @returns {boolean} - True if question has a valid answer
 */
export function hasValidAnswer(answers, questionId) {
    if (!answers) return false;

    const questionData = answers[questionId];
    if (!questionData) return false;

    const answer = questionData.answer;
    // Check if answer exists and is not empty/whitespace
    return answer !== undefined && answer !== null && answer.toString().trim() !== '';
}

/**
 * Calculate questionnaire status based on GENERAL section questions (1-3) ONLY
 * Other sections (Electrical, Hydraulic, Specific) are completely optional
 *
 * @param {Object} answers - The answers object { questionId: { answer: string, observations: string } }
 * @returns {string} - 'Pendiente', 'En progreso', or 'Completado'
 */
export function calculateQuestionnaireStatus(answers) {
    if (!answers) return 'Pendiente';

    // Check if any answers exist at all (in any section)
    const allPossibleQuestions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
    const hasAnyAnswer = allPossibleQuestions.some(questionId => {
        // Check both numeric and string versions of the question ID
        return hasValidAnswer(answers, questionId) || hasValidAnswer(answers, questionId.toString());
    });

    // If no answers at all, status is Pendiente
    if (!hasAnyAnswer) {
        return 'Pendiente';
    }

    // Check if ALL GENERAL questions (1-3) are answered
    const allGeneralQuestionsAnswered = REQUIRED_QUESTIONS.every(questionId => {
        // Check both numeric and string versions of the question ID
        return hasValidAnswer(answers, questionId) || hasValidAnswer(answers, questionId.toString());
    });

    // If all general questions answered, status is Completado
    // (Other sections are optional and don't affect completion status)
    if (allGeneralQuestionsAnswered) {
        return 'Completado';
    }

    // Otherwise, status is En progreso (at least one answer but not all general questions answered)
    return 'En progreso';
}

/**
 * Get list of unanswered required questions
 * @param {Object} answers - The answers object from questionnaire
 * @returns {Array<number>} - Array of question IDs that are unanswered
 */
export function getUnansweredRequiredQuestions(answers) {
    if (!answers) return [...REQUIRED_QUESTIONS];

    return REQUIRED_QUESTIONS.filter(questionId => {
        // Check both numeric and string versions
        return !hasValidAnswer(answers, questionId) && !hasValidAnswer(answers, questionId.toString());
    });
}

/**
 * Build error message for incomplete questionnaire
 * @param {Array} incompleteProducts - Array of { product: object, unansweredQuestions: number[] }
 * @returns {string} - Formatted error message
 */
export function buildChecklistErrorMessage(incompleteProducts) {
    if (!incompleteProducts || incompleteProducts.length === 0) {
        return '';
    }

    let message = 'No se puede cerrar la visita. Faltan respuestas requeridas en el checklist:\n\n';

    incompleteProducts.forEach(({ product, unansweredQuestions }) => {
        const productName = product?.product?.name || product?.model || product?.tnr || 'Producto';
        message += `Producto: ${productName}\n`;

        unansweredQuestions.forEach(questionId => {
            const questionText = QUESTION_TEXTS[questionId] || `Pregunta ${questionId}`;
            message += `  • ${questionText}\n`;
        });

        message += '\n';
    });

    message += 'Por favor complete las preguntas requeridas de la sección "General" antes de cerrar la visita.';

    return message;
}
